import React from "react";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import Selecto from "react-selecto";
import Select from "react-select";
import { Row, Col, Alert, Card, Button, OverlayTrigger, Tooltip } from "react-bootstrap";

import Dropzone from "../../layout/dropzone";
import Modal from "../../layout/modal";
import CsLineIcons from "../../cs-line-icons";

import Items from "./items";

import {
    list as listFiles,
    directories as listDirectories,
    createDirectory,
    download as downloadFile,
    downloadContents,
    downloadMany,
    deleteFile,
    deleteDirectory,
    renameFile,
    renameDirectory,
    moveFile,
    moveDirectory
} from "../../actions/files";

const FileBrowserToolbarButton = ({
    disabled = false,
    onClick,
    tooltip,
    children
}) => (
    <OverlayTrigger
        delay={{ show: 250, hide: 0 }}
        overlay={<Tooltip>{tooltip}</Tooltip>}
        placement="bottom"
    >
        <span className="file-browser-toolbar-button">
            <Button
                type="button"
                variant="outline-primary"
                className="btn-icon btn-icon-only"
                disabled={disabled}
                onClick={onClick}
                aria-label={tooltip}
            >
                {children}
            </Button>
        </span>
    </OverlayTrigger>
);

const CreateFolderIcon = () => (
    <span className="file-browser-create-folder-icon" aria-hidden="true">
        <CsLineIcons icon="folder" />
        <CsLineIcons icon="plus" className="file-browser-create-folder-icon-plus" />
    </span>
);

class Files extends React.Component {
    constructor(props) {
        super(props);

        this.state = {
            loading: false,
            error: null,

            directoryUuid: null,
            directory: null,
            directories: [],
            allDirectories: [],
            breadcrumbs: [
                {
                    uuid: null,
                    name: "Home",
                    path: "\\"
                }
            ],

            items: [],
            selected: [],
            eligible: [],
            contextMenu: {
                show: false,
                x: 0,
                y: 0
            },

            modal: {
                folder: {
                    show: false,
                    name: ""
                },
                destroy: {
                    show: false
                },
                error: {
                    show: false,
                    title: "",
                    body: ""
                },
                rename: {
                    show: false,
                    uuid: null,
                    type: null,
                    name: ""
                },
                move: {
                    show: false,
                    directoryUuid: ""
                }
            }
        };
    }

    componentDidMount = async () => {
        document.addEventListener("click", this.hideContextMenu);
        document.addEventListener("scroll", this.hideContextMenu, true);

        await this.fetchData();
    };

    componentWillUnmount = () => {
        document.removeEventListener("click", this.hideContextMenu);
        document.removeEventListener("scroll", this.hideContextMenu, true);
    };

    componentDidUpdate = async (prevProps) => {
        const changed =
            prevProps.ownerType !== this.props.ownerType ||
            prevProps.ownerUuid !== this.props.ownerUuid ||
            prevProps.contextType !== this.props.contextType ||
            prevProps.contextUuid !== this.props.contextUuid;

        if (changed) {
            this.setState(
                {
                    directoryUuid: null,
                    directory: null,
                    selected: []
                },
                this.fetchData
            );
        }
    };

    buildUploadUrl = () => {
        const {
            ownerType,
            ownerUuid,
            contextType,
            contextUuid
        } = this.props;

        let url = `/files/o/${ownerType}/${ownerUuid}`;

        if (contextType && contextUuid) {
            url += `/c/${contextType}/${contextUuid}`;
        }

        url += "/upload";

        if (this.state.directoryUuid) {
            url += `?directoryUuid=${this.state.directoryUuid}`;
        }

        return url;
    };

    formatBytes = (bytes) => {
        const value = Number(bytes || 0);

        if (!value) {
            return "0 B";
        }

        const units = ["B", "KB", "MB", "GB", "TB"];
        let size = value;
        let unitIndex = 0;

        while (size >= 1024 && unitIndex < units.length - 1) {
            size = size / 1024;
            unitIndex += 1;
        }

        const decimals = size >= 10 || unitIndex === 0 ? 0 : 1;

        return `${size.toFixed(decimals)} ${units[unitIndex]}`;
    };

    formatDisplayDate = (dateValue) => {
        if (!dateValue) {
            return "";
        }

        const date = new Date(dateValue);

        if (isNaN(date.getTime())) {
            return "";
        }

        return date.toLocaleDateString([], {
            month: "short",
            day: "numeric",
            year: "numeric"
        });
    };

    getRecursiveItemCount = (item) => {
        if (!item) {
            return 0;
        }

        if (item.type === "file") {
            return 1;
        }

        /*
         * Preferred backend field:
         * - item.itemCount
         *
         * Other tolerated names:
         * - item.count
         * - item.recursiveCount
         * - item.childCount
         *
         * This assumes the count is descendant contents only,
         * so the selected folder itself counts as 1.
         */
        const recursiveCount =
            item.itemCount ??
            item.count ??
            item.recursiveCount ??
            item.childCount ??
            0;

        return 1 + Number(recursiveCount || 0);
    };

    safeZipBaseName = (value) => {
        return String(value || "Home")
            .replace(/[<>:"/\\|?*]/g, "")
            .split("")
            .filter((character) => character.charCodeAt(0) >= 32)
            .join("")
            .replace(/\s+/g, " ")
            .trim() || "Home";
    };

    getCurrentFolderDownloadName = () => {
        const breadcrumbs = this.state.breadcrumbs || [];
        const folderName = this.state.directory?.name ||
            breadcrumbs[breadcrumbs.length - 1]?.name ||
            "Home";

        return `${this.safeZipBaseName(folderName)}-contents.zip`;
    };

    getSelectedSummary = () => {
        const selected = this.selectedItems;

        if (selected.length === 0) {
            return {
                count: 0,
                size: 0,
                name: "None selected",
                date: ""
            };
        }

        const count = selected.reduce((total, item) => {
            return total + this.getRecursiveItemCount(item);
        }, 0);

        const size = selected.reduce((total, item) => {
            return total + Number(item.size || 0);
        }, 0);

        const name = selected.length === 1
            ? selected[0].name || "Untitled"
            : "Multiple Selected";

        const latestDate = selected.reduce((latest, item) => {
            const itemDate = new Date(item.updatedAt || item.createdAt || 0);

            if (isNaN(itemDate.getTime())) {
                return latest;
            }

            if (!latest || itemDate > latest) {
                return itemDate;
            }

            return latest;
        }, null);

        return {
            count,
            size,
            name,
            date: this.formatDisplayDate(latestDate)
        };
    };

    onItemContextMenu = ({ event, item }) => {
        const isAlreadySelected = this.state.selected.includes(item.uuid);

        this.setState({
            selected: isAlreadySelected
                ? this.state.selected
                : [item.uuid],
            contextMenu: {
                show: true,
                x: event.clientX,
                y: event.clientY
            }
        });
    };

    hideContextMenu = () => {
        if (!this.state.contextMenu.show) {
            return;
        }

        this.setState({
            contextMenu: {
                show: false,
                x: 0,
                y: 0
            }
        });
    };

    onContextMenuAction = async (action) => {
        this.hideContextMenu();

        if (action?.onClick) {
            await action.onClick();
        }
    };

    get scope() {
        return {
            ownerType: this.props.ownerType,
            ownerUuid: this.props.ownerUuid,
            contextType: this.props.contextType,
            contextUuid: this.props.contextUuid
        };
    }

    get selectedItems() {
        return (this.state.items || []).filter((item) => {
            return this.state.selected.includes(item.uuid);
        });
    }

    get selectedFileUuids() {
        return this.selectedItems
            .filter((item) => item.type === "file")
            .map((item) => item.uuid);
    }

    get selectedDirectoryUuids() {
        return this.selectedItems
            .filter((item) => item.type === "directory")
            .map((item) => item.uuid);
    }

    get selectedActions() {
        const actions = [
            {
                caption: "Download",
                onClick: this.onDownloadSelected
            }
        ];

        if (this.state.selected.length === 1) {
            actions.push({
                caption: "Rename",
                onClick: this.onRenameSelected
            });
        }

        actions.push({
            caption: "Move",
            onClick: this.onMoveSelected
        });

        actions.push({
            caption: "Delete",
            onClick: this.onDestroySelected
        });

        return actions;
    }

    fetchData = async () => {
        this.setState({
            loading: true,
            error: null
        });

        try {
            const [response, directoriesResponse] = await Promise.all([
                this.props.listFiles({
                    ...this.scope,
                    directoryUuid: this.state.directoryUuid
                }),
                this.props.listDirectories({
                    ...this.scope
                })
            ]);

            this.setState({
                loading: false,
                directory: response.directory || null,
                directories: response.directories || [],
                allDirectories: directoriesResponse.items || [],
                breadcrumbs: response.breadcrumbs || [
                    {
                        uuid: null,
                        name: "Home",
                        path: "\\"
                    }
                ],
                items: response.items || [],
                selected: []
            });
        } catch (error) {
            this.setState({
                loading: false,
                error: "Unable to load files."
            });
        }
    };

    onSelectItem = ({ item, additive = false }) => {
        if (!item?.uuid) {
            return;
        }

        if (!additive) {
            this.setState({
                selected: [item.uuid],
                contextMenu: {
                    show: false,
                    x: 0,
                    y: 0
                }
            });

            return;
        }

        const selected = [...this.state.selected];
        const index = selected.indexOf(item.uuid);

        if (index === -1) {
            selected.push(item.uuid);
        } else {
            selected.splice(index, 1);
        }

        this.setState({
            selected,
            contextMenu: {
                show: false,
                x: 0,
                y: 0
            }
        });
    };

    onOpenItem = async (item) => {
        if (item.type === "directory") {
            this.setState(
                {
                    directoryUuid: item.uuid,
                    directory: item,
                    selected: []
                },
                this.fetchData
            );

            return;
        }

        await this.props.downloadFile({
            uuid: item.uuid,
            name: item.name
        });
    };

    onBreadcrumbClick = (crumb) => {
        this.setState(
            {
                directoryUuid: crumb.uuid || null,
                selected: []
            },
            this.fetchData
        );
    };

    onPathUp = () => {
        const breadcrumbs = this.state.breadcrumbs || [];

        if (breadcrumbs.length <= 1) {
            return;
        }

        const parent = breadcrumbs[breadcrumbs.length - 2];

        this.setState(
            {
                directoryUuid: parent.uuid || null,
                selected: []
            },
            this.fetchData
        );
    };

    onFolderCreate = () => {
        this.setState({
            modal: {
                ...this.state.modal,
                folder: {
                    show: true,
                    name: ""
                }
            }
        });
    };

    onFolderCancel = () => {
        this.setState({
            modal: {
                ...this.state.modal,
                folder: {
                    show: false,
                    name: ""
                }
            }
        });
    };

    onFolderNameChange = (event) => {
        this.setState({
            modal: {
                ...this.state.modal,
                folder: {
                    ...this.state.modal.folder,
                    name: event.target.value
                }
            }
        });
    };

    onFolderOk = async () => {
        const name = (this.state.modal.folder.name || "").trim();

        if (!name) {
            return;
        }

        await this.props.createDirectory({
            ...this.scope,
            parentUuid: this.state.directoryUuid,
            name
        });

        this.onFolderCancel();

        await this.fetchData();
    };

    onFileUpload = async (response) => {
        if (response?.success === false) {
            this.setState({
                error: response?.error?.message || "Upload failed."
            });

            return;
        }

        await this.fetchData();
    };

    onFilesUpload = async (responses) => {
        const failed = responses.find((response) => response?.success === false);

        if (failed) {
            this.setState({
                error: failed?.error?.message || "One or more uploads failed."
            });
            return;
        }

        await this.fetchData();
    };

    onDragSelect = (event) => {
        const selectedUuids = event.selected
            .map((element) => element.getAttribute("data-file-uuid"))
            .filter(Boolean);

        if (event.inputEvent?.ctrlKey || event.inputEvent?.metaKey) {
            const merged = Array.from(new Set([
                ...this.state.selected,
                ...selectedUuids
            ]));

            this.setState({
                selected: merged
            });

            return;
        }

        this.setState({
            selected: selectedUuids
        });
    };

    onDownloadSelected = async () => {
        if (this.state.loading) {
            return;
        }

        if (this.state.selected.length === 0) {
            await this.onDownloadContents();
            return;
        }

        if (this.state.selected.length === 1 && this.selectedFileUuids.length === 1) {
            const file = this.selectedItems[0];

            await this.props.downloadFile({
                uuid: file.uuid,
                name: file.name
            });

            return;
        }

        await this.props.downloadMany({
            files: this.selectedFileUuids,
            directories: this.selectedDirectoryUuids,
            name: "files.zip"
        });
    };

    onDownloadContents = async () => {
        if (this.state.loading) {
            return;
        }

        const success = await this.props.downloadContents({
            ...this.scope,
            directoryUuid: this.state.directoryUuid,
            name: this.getCurrentFolderDownloadName()
        });

        if (!success) {
            this.setState({
                error: "Unable to download folder contents."
            });
        }
    };

    onRenameSelected = () => {
        if (this.state.selected.length !== 1) {
            return;
        }

        const item = this.selectedItems[0];

        if (!item) {
            return;
        }

        this.setState({
            modal: {
                ...this.state.modal,
                rename: {
                    show: true,
                    uuid: item.uuid,
                    type: item.type,
                    name: item.name || ""
                }
            }
        });
    };

    onRenameCancel = () => {
        this.setState({
            modal: {
                ...this.state.modal,
                rename: {
                    show: false,
                    uuid: null,
                    type: null,
                    name: ""
                }
            }
        });
    };

    onRenameNameChange = (event) => {
        this.setState({
            modal: {
                ...this.state.modal,
                rename: {
                    ...this.state.modal.rename,
                    name: event.target.value
                }
            }
        });
    };

    onRenameOk = async () => {
        const { uuid, type, name } = this.state.modal.rename;
        const cleanName = String(name || "").trim();

        if (!uuid || !type || !cleanName) {
            return;
        }

        this.onRenameCancel();

        if (type === "directory") {
            await this.props.renameDirectory({
                uuid,
                name: cleanName
            });
        } else {
            await this.props.renameFile({
                uuid,
                name: cleanName
            });
        }

        await this.fetchData();
    };

    onMoveSelected = () => {
        if (this.state.selected.length === 0) {
            return;
        }

        this.setState({
            modal: {
                ...this.state.modal,
                move: {
                    show: true,
                    directoryUuid: ""
                }
            }
        });
    };

    onMoveCancel = () => {
        this.setState({
            modal: {
                ...this.state.modal,
                move: {
                    show: false,
                    directoryUuid: ""
                }
            }
        });
    };

    onMoveDirectoryChange = (option) => {
        this.setState({
            modal: {
                ...this.state.modal,
                move: {
                    ...this.state.modal.move,
                    directoryUuid: option?.value || ""
                }
            }
        });
    };

    onMoveOk = async () => {
        const targetDirectoryUuid = this.state.modal.move.directoryUuid || null;

        this.onMoveCancel();

        for (const item of this.selectedItems) {
            if (item.type === "directory") {
                await this.props.moveDirectory({
                    uuid: item.uuid,
                    directoryUuid: targetDirectoryUuid
                });
            } else {
                await this.props.moveFile({
                    uuid: item.uuid,
                    directoryUuid: targetDirectoryUuid
                });
            }
        }

        await this.fetchData();
    };

    onDestroySelected = async () => {
        if (this.state.selected.length === 0) {
            return;
        }

        const containsDirectory = this.selectedItems.some((item) => {
            return item.type === "directory";
        });

        this.setState({
            modal: {
                ...this.state.modal,
                destroy: {
                    show: true,
                    title: "Confirm deletion",
                    body: containsDirectory
                        ? "Confirm deletion of the selected files and folders. Folders must be empty before they can be deleted."
                        : "Confirm deletion of the selected files. This will permanently remove them from storage."
                }
            }
        });
    };

    onDestroyCancel = () => {
        this.setState({
            modal: {
                ...this.state.modal,
                destroy: {
                    show: false
                }
            }
        });
    };

    onDestroyOk = async () => {
        this.onDestroyCancel();

        try {
            for (const item of this.selectedItems) {
                if (item.type === "directory") {
                    await this.props.deleteDirectory({
                        uuid: item.uuid
                    });
                } else {
                    await this.props.deleteFile({
                        uuid: item.uuid
                    });
                }
            }

            await this.fetchData();
        } catch (error) {
            this.setState({
                modal: {
                    ...this.state.modal,
                    error: {
                        show: true,
                        title: "Error",
                        body: error?.response?.data?.error?.message ||
                            error?.message ||
                            "Unable to delete one or more selected items."
                    }
                }
            });
        }
    };

    onErrorOk = () => {
        this.setState({
            modal: {
                ...this.state.modal,
                error: {
                    show: false,
                    title: "",
                    body: ""
                }
            }
        });
    };

    renderBreadcrumbs = () => {
        const breadcrumbs = this.state.breadcrumbs || [];

        return (
            <div className="d-flex flex-wrap align-items-center gap-1">
                {breadcrumbs.map((crumb, index) => (
                    <React.Fragment key={`breadcrumb_${index}`}>
                        {index > 0 ? (
                            <span className="text-muted">/</span>
                        ) : null}

                        <button
                            type="button"
                            className="btn btn-link p-0"
                            onClick={() => this.onBreadcrumbClick(crumb)}
                        >
                            {crumb.name}
                        </button>
                    </React.Fragment>
                ))}
            </div>
        );
    };

    renderFolderModal = () => {
        return (
            <Modal
                show={this.state.modal.folder.show}
                title="Create Folder"
                buttons={[
                    {
                        default: true,
                        caption: "Cancel",
                        onClick: this.onFolderCancel,
                        variant: "secondary",
                        display: true,
                        enabled: true
                    },
                    {
                        caption: "Create",
                        onClick: this.onFolderOk,
                        variant: "outline-primary",
                        display: true,
                        enabled: true
                    }
                ]}
            >
                <div className="mb-3 top-label">
                    <input
                        className="form-control"
                        value={this.state.modal.folder.name}
                        onChange={this.onFolderNameChange}
                        autoFocus
                    />
                    <label>FOLDER NAME</label>
                </div>
            </Modal>
        );
    };

    renderRenameModal = () => {
        return (
            <Modal
                show={this.state.modal.rename.show}
                title="Rename"
                buttons={[
                    {
                        default: true,
                        caption: "cancel",
                        onClick: this.onRenameCancel,
                        variant: "secondary"
                    },
                    {
                        caption: "ok",
                        onClick: this.onRenameOk,
                        variant: "outline-primary"
                    }
                ]}
            >
                <div className="mb-3 top-label">
                    <input
                        className="form-control"
                        value={this.state.modal.rename.name}
                        onChange={this.onRenameNameChange}
                    />
                    <label>NAME</label>
                </div>
            </Modal>
        );
    };

    renderMoveModal = () => {
        const selectedDirectoryUuids = new Set(this.selectedDirectoryUuids);

        const availableDirectories = (this.state.allDirectories || []).filter((directory) => {
            return !selectedDirectoryUuids.has(directory.uuid);
        });
        const directoryOptions = [
            { value: "", label: "Home" },
            ...availableDirectories.map((directory) => ({
                value: directory.uuid,
                label: directory.path,
            })),
        ];
        const selectedDirectoryOption = directoryOptions.find((option) => option.value === this.state.modal.move.directoryUuid) || directoryOptions[0];

        return (
            <Modal
                show={this.state.modal.move.show}
                title="Move"
                buttons={[
                    {
                        default: true,
                        caption: "cancel",
                        onClick: this.onMoveCancel,
                        variant: "secondary"
                    },
                    {
                        caption: "ok",
                        onClick: this.onMoveOk,
                        variant: "outline-primary"
                    }
                ]}
            >
                <div className="mb-3 top-label">
                    <Select
                        inputId="moveDirectoryUuid"
                        classNamePrefix="react-select"
                        value={selectedDirectoryOption}
                        onChange={this.onMoveDirectoryChange}
                        isClearable={false}
                        options={directoryOptions}
                    />
                    <label>DESTINATION FOLDER</label>
                </div>
            </Modal>
        );
    };

    renderSelectedSummary = () => {
        const summary = this.getSelectedSummary();

        return (
            <Card className="mt-3">
                <Card.Body>
                    <div className="mb-2">
                        <div className="text-muted small">
                            Selected
                        </div>
                        <div style={{ fontWeight: 600 }}>
                            {summary.count} {summary.count === 1 ? "item" : "items"}
                        </div>
                    </div>

                    <div className="mb-2">
                        <div className="text-muted small">
                            Size
                        </div>
                        <div style={{ fontWeight: 600 }}>
                            {this.formatBytes(summary.size)}
                        </div>
                    </div>

                    <div className="mb-2">
                        <div className="text-muted small">
                            Name
                        </div>
                        <div
                            className="text-truncate"
                            title={summary.name}
                            style={{ fontWeight: 600 }}
                        >
                            {summary.name}
                        </div>
                    </div>

                    <div>
                        <div className="text-muted small">
                            Date
                        </div>
                        <div style={{ fontWeight: 600 }}>
                            {summary.date || "—"}
                        </div>
                    </div>
                </Card.Body>
            </Card>
        );
    };

    renderContextMenu = () => {
        if (!this.state.contextMenu.show || this.state.selected.length === 0) {
            return null;
        }

        const actions = this.selectedActions || [];

        return (
            <div
                style={{
                    position: "fixed",
                    top: this.state.contextMenu.y,
                    left: this.state.contextMenu.x,
                    zIndex: 9999,
                    minWidth: "180px",
                    background: "#fff",
                    border: "1px solid rgba(0, 0, 0, 0.15)",
                    borderRadius: "8px",
                    boxShadow: "0 8px 24px rgba(0, 0, 0, 0.15)",
                    padding: "6px 0"
                }}
                onClick={(event) => event.stopPropagation()}
                onContextMenu={(event) => event.preventDefault()}
            >
                <div
                    className="px-3 py-1 text-muted"
                    style={{
                        fontSize: ".75rem"
                    }}
                >
                    {this.state.selected.length} selected
                </div>

                {actions.map((action, index) => (
                    <button
                        key={`file_context_action_${index}`}
                        type="button"
                        className="dropdown-item"
                        onClick={() => this.onContextMenuAction(action)}
                        style={{
                            width: "100%",
                            textAlign: "left"
                        }}
                    >
                        {action.caption}
                    </button>
                ))}
            </div>
        );
    };

    render() {
        const canGoUp = !!this.state.directoryUuid;

        return (
            <>
                <Row>
                    <Col xs="12" xl="4">
                        <Dropzone
                            label="Upload files"
                            autoRemove
                            maxSizeBytes={1024 * 1024 * 1024}
                            url={this.buildUploadUrl()}
                            onFileUpload={this.onFileUpload}
                            onFilesUpload={this.onFilesUpload}
                        />

                        {this.renderSelectedSummary()}
                    </Col>

                    <Col xs="12" xl="8">
                        <Row className="align-items-center mb-3">
                            <Col xs="12" md="6">
                                <h2
                                    style={{
                                        height: "var(--small-title-height)",
                                        color: "var(--primary)",
                                        marginTop: "5px"
                                    }}
                                >
                                    {this.renderBreadcrumbs()}
                                </h2>
                            </Col>

                            <Col xs="12" md="6" style={{ textAlign: "right" }}>
                                <div className="file-browser-toolbar">
                                    {canGoUp ? (
                                        <FileBrowserToolbarButton
                                            onClick={this.onPathUp}
                                            tooltip="Go up one folder"
                                        >
                                            <CsLineIcons icon="arrow-top-left" />
                                        </FileBrowserToolbarButton>
                                    ) : null}

                                    <FileBrowserToolbarButton
                                        disabled={this.state.loading}
                                        onClick={this.onDownloadSelected}
                                        tooltip={this.state.selected.length > 0 ? "Download selected files and folders" : "Download folder contents"}
                                    >
                                        <CsLineIcons icon="download" />
                                    </FileBrowserToolbarButton>

                                    <FileBrowserToolbarButton
                                        onClick={this.onFolderCreate}
                                        tooltip="Create folder"
                                    >
                                        <CreateFolderIcon />
                                    </FileBrowserToolbarButton>
                                </div>
                            </Col>
                        </Row>

                        {this.state.error ? (
                            <Alert variant="danger">
                                {this.state.error}
                            </Alert>
                        ) : null}

                        {this.state.loading ? (
                            <div className="text-muted">
                                Loading files...
                            </div>
                        ) : (
                            <>
                                <Selecto
                                    selectableTargets={[".file-selectable"]}
                                    hitRate={0}
                                    selectByClick={false}
                                    selectFromInside={false}
                                    toggleContinueSelect={["ctrl", "meta"]}
                                    onSelectEnd={this.onDragSelect}
                                />

                                <Row>
                                    <Items
                                        selected={this.state.selected}
                                        items={this.state.items}
                                        onOpen={this.onOpenItem}
                                        onSelect={this.onSelectItem}
                                        onContextMenu={this.onItemContextMenu}
                                    />

                                    {this.state.items.length === 0 ? (
                                        <Col xs="12">
                                            <div className="text-muted">
                                                This folder is empty.
                                            </div>
                                        </Col>
                                    ) : null}
                                </Row>
                            </>
                        )}
                    </Col>
                </Row>

                {this.renderFolderModal()}
                {this.renderRenameModal()}
                {this.renderMoveModal()}

                <Modal
                    show={this.state.modal.destroy.show}
                    title={this.state.modal.destroy.title}
                    body={this.state.modal.destroy.body}
                    buttons={[
                        {
                            default: true,
                            caption: "Cancel",
                            onClick: this.onDestroyCancel,
                            variant: "secondary",
                            display: true,
                            enabled: true
                        },
                        {
                            caption: "Delete",
                            onClick: this.onDestroyOk,
                            variant: "outline-primary",
                            display: true,
                            enabled: true
                        }
                    ]}
                />

                <Modal
                    show={this.state.modal.error.show}
                    title={this.state.modal.error.title}
                    body={this.state.modal.error.body}
                    buttons={[
                        {
                            caption: "ok",
                            onClick: this.onErrorOk,
                            variant: "outline-primary"
                        }
                    ]}
                />
                {this.renderContextMenu()}
            </>
        );
    }
}

Files.propTypes = {
    ownerType: PropTypes.string.isRequired,
    ownerUuid: PropTypes.string.isRequired,
    contextType: PropTypes.string,
    contextUuid: PropTypes.string,

    listDirectories: PropTypes.func.isRequired,
    listFiles: PropTypes.func.isRequired,
    createDirectory: PropTypes.func.isRequired,
    downloadFile: PropTypes.func.isRequired,
    downloadContents: PropTypes.func.isRequired,
    downloadMany: PropTypes.func.isRequired,
    deleteFile: PropTypes.func.isRequired,
    deleteDirectory: PropTypes.func.isRequired,

    renameFile: PropTypes.func.isRequired,
    renameDirectory: PropTypes.func.isRequired,

    moveFile: PropTypes.func.isRequired,
    moveDirectory: PropTypes.func.isRequired,
};

Files.defaultProps = {
    contextType: null,
    contextUuid: null
};

const mapStateToProps = (state) => ({
    auth: state.auth
});

export default connect(mapStateToProps, {
    listFiles,
    listDirectories,
    createDirectory,
    downloadFile,
    downloadContents,
    downloadMany,
    deleteFile,
    deleteDirectory,
    renameFile,
    renameDirectory,
    moveFile,
    moveDirectory
})(Files);
